import {FormEvent, useEffect, useRef, useState} from "react";
import {Alert, Form} from "react-bootstrap";
import {Link, useParams} from "react-router";
import type {Editor} from "@tiptap/react";
import {DocumentationApi} from "../../api/DocumentationApi";
import {DocumentationImage} from "../../api/models";
import {useAsync} from "../../components/useAsync";
import {ErrorAlert} from "../../components/ErrorAlert";
import {LoadingSpinner} from "../../components/LoadingSpinner";
import {PageHeader} from "../../components/PageHeader";
import {SubmitButton} from "../../components/SubmitButton";
import {RichTextEditor} from "../../components/RichTextEditor";
import {DocumentationImageLibrary} from "./product/DocumentationImageLibrary";
import {docImageName, docImageReferences, withDocImageLinks} from "./product/docImages";

// A product's documentation for signed-in users. Its headings (Heading and Subheading) make the contents. Its images
// come from the product's image library, below the editor.
export function ProductDocumentationEditPage() {
    const {productId = ''} = useParams();
    const {data: documentation, error: loadError, loading, setData} = useAsync(() => DocumentationApi.get(productId, true), [productId]);
    const {data: images, error: imagesError, reload: reloadImages} = useAsync(() => DocumentationApi.listImages(productId), [productId]);
    const [content, setContent] = useState('');
    const [busy, setBusy] = useState(false);
    const [saved, setSaved] = useState(false);
    const [missing, setMissing] = useState<string[]>([]);
    const [error, setError] = useState<unknown>();
    const editor = useRef<Editor | null>(null);

    useEffect(() => {
        if (documentation) {
            setContent(documentation.content);
        }
    }, [documentation]);

    // HTML pasted into the source view may refer to images as doc-image:{name}: shown by their links (saving stores ids).
    const change = (value: string) => {
        setContent(withDocImageLinks(value, images ?? []));
        setSaved(false);
    };
    // Once the library has loaded or changed, references to its images show too.
    useEffect(() => {
        if (images) {
            setContent(x => withDocImageLinks(x, images));
        }
    }, [images]);

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        setBusy(true);
        setSaved(false);
        setError(undefined);
        try {
            const result = await DocumentationApi.save(productId, content);
            setData(result);
            setMissing(result.missingImages ?? []);
            setSaved(true);
            reloadImages();
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
        }
    };

    const insert = (image: DocumentationImage) => {
        editor.current?.chain().focus().setImage({ src: image.src, alt: image.altText, width: image.width, height: image.height } as never).run();
    };

    // References the library has no image for: saving leaves them out.
    const unknown = [...new Set(docImageReferences(content))];
    const back = `/admin/products/${encodeURIComponent(productId)}`;
    return (
        <>
            <div className="mb-2"><Link to={back} className="small"><i className="bi bi-arrow-left me-1"></i>Back to the product</Link></div>
            <ErrorAlert error={loadError} messages={{ 404: 'There is no such product.' }}/>
            {loading && !documentation && <LoadingSpinner/>}
            {documentation && (
                <>
                    <PageHeader title={`${documentation.productName} documentation`}>
                        <Link to={`/docs/${encodeURIComponent(documentation.sku)}`} className="btn btn-outline-secondary">View</Link>
                    </PageHeader>
                    <p className="text-body-secondary">
                        Signed-in users read this at <code>/docs/{documentation.sku}</code>, once the product is published. Headings and
                        subheadings make its table of contents. Add images with the image button, or from the image library below:
                        images pasted from elsewhere aren't kept. To paste in HTML, use the editor's HTML source view; it can show the
                        library's images as <code>&lt;img src="doc-image:<em>name</em>" alt="…"&gt;</code>.
                    </p>
                    <ErrorAlert error={error}/>
                    {saved && <Alert variant="success" dismissible onClose={() => setSaved(false)}>Saved.</Alert>}
                    {saved && missing.length > 0 && (
                        <Alert variant="warning">
                            These images aren't in the library, so they were left out: {missing.map(x => <code key={x} className="me-2">{x}</code>)}
                            Upload them, then paste the HTML in again.
                        </Alert>
                    )}
                    <Form onSubmit={submit} className="docs-editor">
                        <Form.Label id="docs-content-label" className="visually-hidden">Documentation</Form.Label>
                        <RichTextEditor labelledBy="docs-content-label" value={content} onChange={change}
                                        onEditor={x => { editor.current = x; }}
                                        onUploadImage={async (file, onProgress) => {
                                            const image = await DocumentationApi.uploadImage(productId, file, onProgress, { name: docImageName(file.name) });
                                            reloadImages();
                                            return image;
                                        }}/>
                        {unknown.length > 0 && (
                            <div className="small text-warning-emphasis mt-2" role="status">
                                Not in the image library (saving leaves them out): {unknown.map(x => <code key={x} className="me-2">{x}</code>)}
                            </div>
                        )}
                        <div className="mt-3 d-flex align-items-center gap-3">
                            <SubmitButton busy={busy}>Save</SubmitButton>
                            {content !== documentation.content && <span className="small text-body-secondary">Unsaved changes</span>}
                        </div>
                    </Form>
                    <ErrorAlert error={imagesError}/>
                    {images && <DocumentationImageLibrary productId={productId} images={images} onChanged={reloadImages} onInsert={insert}/>}
                </>
            )}
        </>
    );
}
